import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Pagination,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/apiService';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const Committees: React.FC = () => {
  const navigate = useNavigate();
  const [committees, setCommittees] = useState<any[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, per_page: 50, has_next: false });
  const [loading, setLoading] = useState(false);
  const [chamber, setChamber] = useState('');

  const fetchData = async (page: number, chamberFilter?: string) => {
    setLoading(true);
    try {
      const limit = pagination.per_page;
      const offset = (page - 1) * limit;
      let resp;
      if (chamberFilter) {
        resp = await apiService.getCommitteesByChamber(chamberFilter, { limit, offset });
      } else {
        resp = await apiService.getCommittees({ limit, offset });
      }
      setCommittees(resp.data?.committees || []);
      setPagination(resp.data?.pagination || { total: 0, page, per_page: limit, has_next: false });
    } catch (e) {
      console.error('Failed to load committees', e);
      setCommittees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(1, chamber || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chamber]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(page, chamber || undefined);
  };

  const handleView = (c: any) => {
    if (!c || !c.chamber || !c.code) return;
    navigate(`/committees/${c.chamber}/${c.code}`);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Committees
      </Typography>

      <Box display="flex" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Chamber</InputLabel>
          <Select value={chamber} label="Chamber" onChange={(e) => setChamber(String(e.target.value))}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="house">House</MenuItem>
            <MenuItem value="senate">Senate</MenuItem>
            <MenuItem value="joint">Joint</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading ? (
        <LoadingSpinner message="Loading committees..." />
      ) : (
        <>
          <Grid container spacing={2}>
            {committees.map((c, idx) => (
              <Grid item xs={12} md={6} lg={4} key={idx}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      {c.name || c.code}
                    </Typography>
                    <Box display="flex" gap={1} mb={1}>
                      {c.chamber && <Chip label={String(c.chamber).toUpperCase()} size="small" />}
                      {c.code && <Chip label={c.code} size="small" variant="outlined" />}
                    </Box>
                  </CardContent>
                  <CardActions>
                    <Button size="small" onClick={() => handleView(c)}>View Details</Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {pagination.total > pagination.per_page && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.ceil(pagination.total / pagination.per_page)}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Committees;
